/**
 * BrowserHeadsetToggle — v3.21-1753
 * =================================
 * One-click mode control for the optional Browser Headset feature.
 *
 * States:
 *   INACTIVE  →  "🎧 Use Browser Headset"      (default; clicking activates)
 *   CONNECTING → "Connecting Headset…"
 *   ACTIVE    →  "✓ Browser Headset Active"    (clicking returns to Standard)
 *   ERROR     →  "Browser Headset Unavailable" (clicking retries; Standard
 *                                                Calling remains available)
 *
 * The button does NOT expose Twilio secrets, does NOT auto-activate,
 * and does NOT register a browser endpoint unless the operator clicks.
 */
import { useCallback, useState } from 'react';
import { Headphones, Loader2, Check, TriangleAlert, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useCallMode } from '../hooks/useCallMode';
import { useBrowserHeadsetDevice } from '../hooks/useBrowserHeadsetDevice';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';

const BrowserHeadsetToggle = ({ compact = false }) => {
  const { user } = useAuth();
  const uid = user?.id || user?.user_id;
  const { isBrowserHeadset, setStandard, setBrowserHeadset } = useCallMode(uid);
  const dev = useBrowserHeadsetDevice();
  const [busy, setBusy] = useState(false);

  const onToggle = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (isBrowserHeadset || dev.isActive) {
        // Switch back to Standard Calling.
        await dev.disable();
        setStandard();
        toast.success('Switched to Standard Calling.');
      } else {
        setBrowserHeadset();  // persist opt-in immediately
        const r = await dev.enable();
        if (!r.ok) {
          setStandard();      // roll back — Standard remains available
          toast.error(r.error || 'Browser Headset unavailable.');
        } else {
          toast.success('Browser Headset active.');
        }
      }
    } finally {
      setBusy(false);
    }
  }, [busy, dev, isBrowserHeadset, setBrowserHeadset, setStandard]);

  let label = <>🎧 Use Browser Headset</>;
  let cls = 'bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-200';
  let Icon = Headphones;
  if (dev.isConnecting || busy) {
    label = <>Connecting Headset…</>;
    Icon = Loader2;
    cls = 'bg-slate-100 text-slate-800 border-slate-200';
  } else if (dev.isActive || isBrowserHeadset) {
    label = <>Browser Headset Active</>;
    Icon = Check;
    cls = 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-700';
  } else if (dev.isError) {
    label = <>Browser Headset Unavailable</>;
    Icon = TriangleAlert;
    cls = 'bg-amber-50 hover:bg-amber-100 text-amber-800 border-amber-200';
  }

  const size = compact ? 'h-8 px-2 text-[11px]' : 'h-9 px-3 text-xs';

  return (
    <div className="flex items-center gap-1.5" data-testid="browser-headset-toggle-wrap">
      <button
        type="button"
        onClick={onToggle}
        className={`inline-flex items-center gap-1.5 rounded-lg border font-medium transition-colors ${cls} ${size}`}
        data-testid="browser-headset-toggle-btn"
        aria-pressed={dev.isActive || isBrowserHeadset}
        title={dev.isActive ? 'Return to Standard Calling' : 'Route calls through your USB/wireless headset'}
      >
        <Icon className={`h-3.5 w-3.5 ${dev.isConnecting ? 'animate-spin' : ''}`} />
        {label}
      </button>

      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`inline-flex items-center gap-1 rounded-lg border bg-white hover:bg-slate-50 text-slate-600 font-medium transition-colors ${size} border-slate-200`}
            data-testid="browser-headset-audio-settings-btn"
            title="Audio Settings"
          >
            <Settings2 className="h-3.5 w-3.5" />
            {!compact && <span>Audio</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-3" align="end" data-testid="browser-headset-audio-settings-popover">
          <div className="text-xs font-semibold text-slate-800 mb-2">Audio Settings</div>
          <p className="text-[11px] text-slate-500 mb-2">
            Applied only in Browser Headset mode. Standard Calling is unaffected.
          </p>
          <div className="space-y-2">
            <DevicePicker
              label="Microphone" testid="audio-mic-select"
              devices={dev.inputs}  value={dev.selected.mic}    onChange={dev.setMic}
              onOpen={dev.refreshDevices}
            />
            <DevicePicker
              label="Call Audio" testid="audio-output-select"
              devices={dev.outputs} value={dev.selected.output} onChange={dev.setOutput}
              onOpen={dev.refreshDevices}
            />
            <DevicePicker
              label="Ringtone" testid="audio-ringer-select"
              devices={dev.outputs} value={dev.selected.ringer} onChange={dev.setRinger}
              onOpen={dev.refreshDevices}
            />
            <Button
              variant="outline"
              className="w-full h-8 text-xs mt-1"
              onClick={async () => {
                const r = await dev.testHeadset();
                if (r.ok) toast.success('Test tone played.');
                else toast.error(r.error || 'Test failed.');
              }}
              data-testid="audio-test-headset-btn"
            >
              Test Headset
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const DevicePicker = ({ label, testid, devices, value, onChange, onOpen }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</div>
    <Select
      value={value || 'default'}
      onValueChange={(v) => onChange(v === 'default' ? null : v)}
      onOpenChange={(o) => { if (o) onOpen?.(); }}
    >
      <SelectTrigger className="h-8 text-xs" data-testid={testid}>
        <SelectValue placeholder="System default" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="default" className="text-xs">System default</SelectItem>
        {devices?.map(d => (
          <SelectItem key={d.deviceId} value={d.deviceId} className="text-xs">
            {d.label || `Device ${d.deviceId.slice(0, 6)}`}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

export default BrowserHeadsetToggle;
